import { fromZonedTime, formatInTimeZone, getTimezoneOffset } from "date-fns-tz";
import { formatDateWithOptionsBs, formatTimeWithOptionsBs } from "@/lib/format";

// Single hardcoded application timezone. The platform serves Bosnia-Herzegovina
// and all wall-clock display/input is in Sarajevo time. Per-user timezones are
// out of scope; storage is always UTC.
export const APP_TIMEZONE = "Europe/Sarajevo";

// Convert a Sarajevo wall-clock date+time pair to a UTC ISO string with `Z`.
// Used at the booking write boundary, where the client sends "YYYY-MM-DD" and "HH:mm"
// understood as Sarajevo local time.
//
// Throws on the spring-forward DST gap (e.g. 2026-03-29 02:30 doesn't exist in
// Sarajevo because clocks jump 02:00 → 03:00). The fall-back duplicate hour is
// accepted silently — both occurrences round-trip to the same wall-clock string,
// and the slot grid never offers those minutes anyway.
export function wallClockToUtc(date: string, time: string): string {
  const utcIso = fromZonedTime(`${date}T${time}:00`, APP_TIMEZONE).toISOString();
  const { hour, minute } = formatHourMinuteInZone(utcIso);
  const roundTrip = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  if (roundTrip !== time) {
    throw new Error(
      `wallClockToUtc: ${date}T${time} does not exist in ${APP_TIMEZONE} (DST gap)`,
    );
  }
  return utcIso;
}

// Display formatters: take any Date-parseable string (UTC ISO with Z is the
// expected post-migration format) and render in Sarajevo wall-clock with Bosnian labels.
export function formatDateBs(utcIso: string): string {
  return formatDateWithOptionsBs(utcIso, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: APP_TIMEZONE,
  });
}

export function formatTimeBs(utcIso: string): string {
  return formatTimeWithOptionsBs(utcIso, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: APP_TIMEZONE,
    hour12: false,
  });
}

// Numeric extraction of wall-clock hour/minute in the app TZ. Used by
// slot generation when comparing booked sessions to slot ranges (which are
// expressed as Sarajevo wall-clock minutes-from-midnight).
export function formatHourMinuteInZone(utcIso: string): { hour: number; minute: number } {
  const [hh, mm] = formatInTimeZone(utcIso, APP_TIMEZONE, "HH:mm").split(":");
  return { hour: Number(hh), minute: Number(mm) };
}

// For a given Sarajevo calendar day (YYYY-MM-DD), return the [startUtc, endUtc)
// half-open UTC range that the day spans. Replaces `DATE(scheduledAt) = ?` queries.
export function utcRangeForLocalDate(date: string): { startUtc: string; endUtc: string } {
  const startUtc = fromZonedTime(`${date}T00:00:00`, APP_TIMEZONE).toISOString();
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const nextDate = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
  const endUtc = fromZonedTime(`${nextDate}T00:00:00`, APP_TIMEZONE).toISOString();
  return { startUtc, endUtc };
}

// Subtract `hours` Sarajevo wall-clock hours from a UTC moment, returning UTC ISO.
// DST-correct: if the result crosses a DST transition, the returned moment is at
// the same wall-clock time-of-day, not the same absolute time.
//
// Example: subWallClockHours("2026-03-29T08:00:00.000Z", 24) returns the UTC
// moment for 2026-03-28 10:00 Sarajevo (CET, UTC+1) = "2026-03-28T09:00:00.000Z".
// The UTC delta is 23h; the wall-clock delta is 24h.
export function subWallClockHours(utcIso: string, hours: number): string {
  const localIso = formatInTimeZone(utcIso, APP_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ss");
  const [datePart, timePart] = localIso.split("T") as [string, string];
  const [y, mo, d] = datePart.split("-").map(Number) as [number, number, number];
  const [h, mi, s] = timePart.split(":").map(Number) as [number, number, number];
  // Pseudo-UTC: encode the wall-clock components into a Date as if they were UTC,
  // do hour arithmetic on those (DST-blind by design — wall-clock subtraction),
  // then reformat and convert back.
  const pseudoMs = Date.UTC(y, mo - 1, d, h, mi, s) - hours * 60 * 60 * 1000;
  const pseudo = new Date(pseudoMs);
  const yy = pseudo.getUTCFullYear();
  const moo = String(pseudo.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(pseudo.getUTCDate()).padStart(2, "0");
  const hh = String(pseudo.getUTCHours()).padStart(2, "0");
  const mm = String(pseudo.getUTCMinutes()).padStart(2, "0");
  const ss = String(pseudo.getUTCSeconds()).padStart(2, "0");
  return fromZonedTime(`${yy}-${moo}-${dd}T${hh}:${mm}:${ss}`, APP_TIMEZONE).toISOString();
}

// Decompose a UTC ISO string into Sarajevo wall-clock date+time parts.
// Inverse of `wallClockToUtc`. Use this on the server (and anywhere we need
// to render a UTC moment back into editable form fields) — never rely on
// `Date.getHours()` which uses the runtime's local TZ and corrupts edits
// from non-Sarajevo browsers.
export function utcToWallClock(utcIso: string): { date: string; time: string } {
  const [date, time] = formatInTimeZone(utcIso, APP_TIMEZONE, "yyyy-MM-dd'T'HH:mm").split("T") as [
    string,
    string,
  ];
  return { date, time };
}

// Today's calendar date (YYYY-MM-DD) in the app timezone.
// Use this instead of `new Date().toISOString().split("T")[0]` which returns
// the UTC calendar date and is off by 1 around Sarajevo midnight.
export function todayInZone(now: Date = new Date()): string {
  return formatInTimeZone(now, APP_TIMEZONE, "yyyy-MM-dd");
}

// Add `n` calendar days to a YYYY-MM-DD string. Pure string arithmetic on the
// (year, month, day) tuple via UTC — no timezone or DST involvement. Replaces
// `new Date(s + "T00:00:00"); d.setDate(d.getDate() + n)` which routes through
// the runtime's local TZ and silently corrupts the result across DST boundaries.
// `n` may be negative.
export function addCalendarDays(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  const yy = t.getUTCFullYear();
  const mm = String(t.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(t.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

// Add `hours` Sarajevo wall-clock hours to a UTC moment. Inverse sign of
// `subWallClockHours`. Use this for "X wall-clock hours in the future" math —
// in particular cron reminder targets, which must be DST-aware (e.g. 24h ahead
// is 23 UTC hours on the spring-forward day, 25 on fall-back).
export function addWallClockHours(utcIso: string, hours: number): string {
  return subWallClockHours(utcIso, -hours);
}

// Return [startUtc, endUtc) for a Sarajevo calendar month. `month` is 1-12.
// Replaces ad-hoc `new Date(y, m, 1).toISOString()` constructions that route
// through the server's local TZ.
export function utcRangeForLocalMonth(year: number, month: number): {
  startUtc: string;
  endUtc: string;
  startDate: string;
  endDate: string;
} {
  const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  const endDate = `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`;
  return {
    startUtc: fromZonedTime(`${startDate}T00:00:00`, APP_TIMEZONE).toISOString(),
    endUtc: fromZonedTime(`${endDate}T00:00:00`, APP_TIMEZONE).toISOString(),
    startDate,
    endDate,
  };
}

// Year+month tuple (1-12) for "now" in Sarajevo. Use instead of
// `new Date().getMonth()` which uses the runtime's local TZ.
export function currentYearMonthInZone(): { year: number; month: number } {
  const [y, m] = formatInTimeZone(new Date(), APP_TIMEZONE, "yyyy-MM").split("-").map(Number) as [
    number,
    number,
  ];
  return { year: y, month: m };
}

// ── Prikaz za posjetioca iz druge vremenske zone ──
// Termini se biraju i prikazuju po Sarajevu. Klijentu čiji sat u tom trenutku
// pokazuje nešto drugo (UK, Turska, SAD...) pokazujemo i njegovo vrijeme, da
// "18:00" ne pročita kao svoje lokalno vrijeme. Zemlje sa istim satom kao
// Sarajevo (većina dijaspore u EU) ne vide ništa novo.

/** IANA zona browsera, ili null kad nije dostupna. */
export function viewerTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export interface ViewerWallClock {
  /** "HH:mm" na satu posjetioca. */
  time: string;
  /** -1 = kod posjetioca je prethodni dan, 1 = sljedeći dan. */
  dayOffset: -1 | 0 | 1;
  /** Koliko minuta je sat posjetioca ispred (+) ili iza (-) Sarajeva. */
  diffMinutes: number;
}

/**
 * Vrijeme posjetioca za sarajevski termin `date` + `time`, ili null kad mu sat
 * u tom trenutku pokazuje isto što i Sarajevo (ili zona nije poznata/validna).
 */
export function viewerWallClock(date: string, time: string, viewerTz: string | null): ViewerWallClock | null {
  if (!viewerTz || viewerTz === APP_TIMEZONE) return null;
  try {
    const utcIso = wallClockToUtc(date, time);
    const instant = new Date(utcIso);
    const diffMinutes = Math.round(
      (getTimezoneOffset(viewerTz, instant) - getTimezoneOffset(APP_TIMEZONE, instant)) / 60_000,
    );
    if (!Number.isFinite(diffMinutes) || diffMinutes === 0) return null;
    const [viewerDate, viewerTime] = formatInTimeZone(utcIso, viewerTz, "yyyy-MM-dd HH:mm").split(" ") as [string, string];
    const dayOffset = viewerDate < date ? -1 : viewerDate > date ? 1 : 0;
    return { time: viewerTime, dayOffset, diffMinutes };
  } catch {
    return null;
  }
}
